import { useId, useMemo, useRef, useState } from 'react';
import { cn } from './cn.js';
import { useLayer } from './layer.js';
import { folded } from './fold.js';
import { CompactTriggerButton, type CompactTrigger } from './CompactTrigger.js';
import { Icon } from './Icon.js';
import { Popover, PopoverContent, PopoverTrigger } from './primitives/popover.js';

export interface MultiSelectOption {
  value: string;
  label: string;
}

type Order = 'asc' | 'desc';

export interface MultiSelectProps {
  label: string;
  options: readonly MultiSelectOption[];
  selected: readonly string[];
  onChange: (selected: string[]) => void;
  /** O texto do gatilho quando nada está marcado — "Todas", "Todos". */
  placeholder?: string;
  searchPlaceholder?: string;
  /** Quando a busca não acha nada dentro das opções. */
  emptyMessage?: string;
  /** Esconde o rótulo visualmente e o mantém para leitor de tela. */
  hiddenLabel?: boolean;
  /**
   * O estado carregando: as opções ainda estão a caminho, e "Nada encontrado"
   * mentiria sobre uma lista vazia por enquanto, e não por não haver nada
   * cadastrado.
   */
  loading?: boolean;
  disabled?: boolean;
  /**
   * Onde a lista abre. `painel` (o padrão) é o painel de filtros, abaixo da
   * faixa do topo — `z-10`. `modal` é dentro de um diálogo: a lista precisa
   * ficar acima do véu (`z-50`) e rolar, e o Dialog trava o scroll de tudo
   * que está fora dele — o mesmo conserto do `SearchSelect`.
   */
  layer?: 'painel' | 'modal';
  /**
   * Uma pastilha de ícone no lugar do campo (`CompactTrigger`): o rótulo vai
   * para o leitor de tela, e o texto da pastilha, para o `title`.
   */
  compact?: CompactTrigger;
}

/**
 * Escolha de várias opções entre muitas, com busca e ordenação.
 *
 * O `Select` da marca é de propósito um `<select>` nativo — de uma opção só,
 * porque o nativo resolve teclado e leitor de tela de graça. Ele para de servir
 * quando a pergunta muda para "quais destas", porque `<select multiple>` não
 * tem como buscar dentro das opções nem mostrar o que já foi marcado: é aqui,
 * e não em `Select`, que o `DESIGN.md` autoriza primitivo — a camada (abrir,
 * fechar, devolver o foco) é do `Popover`; a busca e a lista são nossas.
 *
 * Nasceu da mesa de demandas — situação, categoria, departamento, prioridade e
 * as duas pessoas (quem atende e quem abriu) precisavam do mesmo comportamento
 * — e mora aqui porque a pergunta se repete em qualquer filtro com lista longa,
 * não só nesse.
 *
 * A seleção sai sempre na ORDEM DE `options`, nunca na ordem do clique: é o que
 * o grupo de caixas do comercial já fazia por um motivo que vale aqui igual — a
 * chave de cache da consulta não pode distinguir `a,b` de `b,a`. Aquele grupo
 * saiu em 18/09, quando o último filtro de caixas virou este componente.
 */
export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  placeholder = 'Todas',
  searchPlaceholder = 'Buscar…',
  emptyMessage = 'Nada encontrado.',
  hiddenLabel = false,
  loading = false,
  disabled = false,
  layer: layerProp,
  compact,
}: MultiSelectProps) {
  const layer = useLayer(layerProp);
  const idLabel = useId();
  const idTrigger = useId();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [order, setOrder] = useState<Order>('asc');
  const searchRef = useRef<HTMLInputElement>(null);

  const byValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);

  const visible = useMemo(() => {
    const needle = folded(term.trim());
    const matched = needle
      ? options.filter((o) => folded(o.label).includes(needle))
      : options.slice();
    matched.sort((a, b) =>
      order === 'asc'
        ? a.label.localeCompare(b.label, 'pt-BR')
        : b.label.localeCompare(a.label, 'pt-BR'),
    );
    return matched;
  }, [options, term, order]);

  const toggle = (value: string) => {
    const checked = selected.includes(value);
    onChange(
      options
        .map((o) => o.value)
        .filter((v) => (v === value ? !checked : selected.includes(v))),
    );
  };

  /*
   * O gatilho diz QUANTAS estão marcadas, nunca a lista inteira ao lado dele
   * — nem em pílulas removíveis abaixo. Quem quer ver e desmarcar abre o
   * `Popover`: os itens já vêm com o `checkbox` marcado, e é lá que se tira.
   * Uma fileira de pílulas crescia com a seleção e empurrava as
   * trilhas vizinhas do painel de `Filters`, que são de altura fixa.
   */
  const summary =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? (byValue.get(selected[0]!)?.label ?? placeholder)
        : `${selected.length} selecionadas`;

  return (
    <div className={compact ? 'inline-flex' : 'flex flex-col gap-1.5'}>
      <span
        id={idLabel}
        className={cn(
          'text-xs font-medium text-texto',
          (hiddenLabel || compact) && 'sr-only',
        )}
      >
        {label}
      </span>

      <Popover
        modal={layer === 'modal'}
        open={open}
        onOpenChange={(next) => {
          if (disabled) return;
          setOpen(next);
          if (!next) setTerm('');
        }}
      >
        <PopoverTrigger asChild>
          {compact ? (
            <CompactTriggerButton compact={compact} id={idTrigger} disabled={disabled} />
          ) : (
            <button
              /* Veste um campo: o foco dele é o de dentro, não o anel do botão. */
              data-campo=""
              type="button"
              id={idTrigger}
              disabled={disabled}
              aria-labelledby={`${idLabel} ${idTrigger}`}
              className={cn(
                'flex h-9 w-full items-center justify-between gap-2 rounded-borda border bg-superficie px-3 text-xs',
                'text-texto pointer-coarse:h-11',
                'transition-[border-color,background-color] active:bg-superficie-suave',
                'border-borda-forte hover:border-texto-suave',
                'disabled:cursor-not-allowed disabled:bg-superficie-suave disabled:opacity-60',
              )}
            >
              <span
                className={cn(
                  'truncate text-left',
                  selected.length === 0 && 'text-texto-suave',
                )}
              >
                {summary}
              </span>
              <Icon nome="seta-baixo" size={16} className="shrink-0 text-texto-suave" />
            </button>
          )}
        </PopoverTrigger>

        <PopoverContent
          align="start"
          /*
           * `z-10` pelo mesmo motivo do painel de filtros, que é de onde esta
           * lista mais abre: a faixa do topo é `z-20`, e o `z-50` do primitivo
           * a cobria ao rolar a página. Ela continua acima do painel que a
           * abriu porque o portal dela monta depois, e empate de `z` desenha na
           * ordem do DOM.
           */
          className={cn(
            'p-0',
            layer === 'modal'
              ? compact
                ? 'z-50 w-[16rem] max-w-[calc(100vw-2rem)]'
                : 'z-50 w-(--radix-popover-trigger-width) max-w-[calc(100vw-2rem)]'
              : 'z-10 w-[16rem]',
          )}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            searchRef.current?.focus();
          }}
        >
          <div className="border-b border-borda p-2">
            <div className="relative">
              <Icon
                nome="busca"
                size={16}
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-texto-suave"
              />
              <input
                ref={searchRef}
                type="text"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder={searchPlaceholder}
                className={cn(
                  'h-9 w-full rounded-borda border border-borda-forte bg-superficie pl-8 pr-2 text-sm',
                  'text-texto placeholder:text-texto-suave',
                )}
              />
            </div>

            <div className="mt-2 flex items-center gap-1.5 text-xs">
              <span className="text-texto-suave">Ordenar</span>
              <button
                type="button"
                onClick={() => setOrder(order === 'asc' ? 'desc' : 'asc')}
                aria-label={
                  order === 'asc'
                    ? 'Ordenado de A a Z — trocar para Z a A'
                    : 'Ordenado de Z a A — trocar para A a Z'
                }
                title={order === 'asc' ? 'A → Z' : 'Z → A'}
                className="rounded-borda p-1 text-texto-suave hover:bg-superficie-suave hover:text-texto"
              >
                <Icon nome={order === 'asc' ? 'seta-cima' : 'seta-baixo'} size={16} />
              </button>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto p-1">
            {loading && (
              <p className="px-2 py-3 text-center text-sm text-texto-suave">
                Carregando…
              </p>
            )}
            {!loading && visible.length === 0 && (
              <p className="px-2 py-3 text-center text-sm text-texto-suave">
                {emptyMessage}
              </p>
            )}
            {!loading &&
              visible.map((option) => (
                <label
                  key={option.value}
                  className="flex min-h-9 cursor-pointer items-center gap-2 rounded-borda px-2 text-sm text-texto hover:bg-superficie-suave"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(option.value)}
                    onChange={() => toggle(option.value)}
                    className="size-4 shrink-0 rounded border-borda-forte accent-primaria-texto"
                  />
                  <span className="truncate">{option.label}</span>
                </label>
              ))}
          </div>

          {selected.length > 0 && (
            <div className="border-t border-borda p-1.5">
              <button
                type="button"
                onClick={() => onChange([])}
                className="w-full rounded-borda px-2 py-1.5 text-center text-xs font-semibold text-texto-suave hover:bg-superficie-suave hover:text-texto"
              >
                Limpar seleção
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
